import { motion } from "framer-motion";
import { Plus, BadgePercent } from "lucide-react";
import { MENU, IMAGES, ALLERGENS, ZUSATZ, eur } from "../lib/menuData";
import { useCart } from "../context/CartContext";

const Item = ({ item }) => {
  const { add } = useCart();
  return (
    <div className="flex items-center gap-3 group">
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <h4 className="font-serif text-xl text-teal-dark leading-tight">
            <span className="text-teal/40 text-base mr-1.5">{item.n}.</span>{item.name}
          </h4>
          {item.a && <span className="text-[0.62rem] text-teal/40 tracking-wide">[{item.a}]</span>}
        </div>
        {item.desc && <p className="text-teal/60 text-sm mt-1">{item.desc}</p>}
      </div>
      <span className="font-serif text-xl text-teal whitespace-nowrap">{eur(item.p)}</span>
      <button data-testid={`add-${item.n}`} onClick={() => add(item)}
        aria-label={`${item.name} in den Warenkorb`}
        className="h-8 w-8 shrink-0 rounded-full bg-teal text-cream flex items-center justify-center hover:bg-teal-dark hover:scale-110 transition-all">
        <Plus size={16} />
      </button>
    </div>
  );
};

const Category = ({ cat, i }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay: (i % 2) * 0.1, ease: [0.16, 1, 0.3, 1] }}
    data-testid={`menu-category-${cat.id}`}
    className="break-inside-avoid mb-12"
  >
    <div className="flex items-center gap-4 mb-7">
      <span className="font-serif text-4xl text-teal/25 leading-none">{cat.roman}</span>
      <h3 className="font-serif text-3xl md:text-4xl text-teal-dark">{cat.title}</h3>
    </div>
    <div className="space-y-6">
      {cat.items.map((it) => <Item key={it.n} item={it} />)}
    </div>
  </motion.div>
);

export const MenuSection = () => (
  <section id="speisekarte" className="relative bg-cream text-teal-dark py-24 md:py-36">
    <div className="max-w-7xl mx-auto px-5 md:px-10">
      <div className="grid lg:grid-cols-3 gap-10 items-end mb-10">
        <div className="lg:col-span-2">
          <div className="overline flex items-center gap-3 mb-5" style={{ color: "#123F41" }}>
            <span className="h-px w-8 inline-block" style={{ background: "#123F41" }} />
            Kapitel II — Speisekarte
          </div>
          <h2 className="font-serif text-5xl sm:text-6xl md:text-7xl text-teal-dark leading-[0.95]">
            Tamilische Küche,<br /><span className="italic text-teal">frisch zubereitet.</span>
          </h2>
          <p className="mt-5 text-teal/60 max-w-md">Tippe auf das <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-teal text-cream align-middle"><Plus size={12} /></span> um Gerichte in den Warenkorb zu legen – mit 10 % Online-Rabatt, Lieferung ab 20 €.</p>
        </div>
        <div className="relative h-56 lg:h-64 overflow-hidden rounded-sm border border-teal/15">
          <img src={IMAGES.menuHighlight} alt="Srilankisches Gericht" className="h-full w-full object-cover" />
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        data-testid="discount-banner"
        className="mb-16 flex flex-col sm:flex-row items-center gap-4 sm:gap-6 rounded-sm border-2 border-gold bg-teal-dark px-6 py-6 sm:px-10 shadow-[0_10px_50px_-15px_rgba(11,43,45,0.6)]"
      >
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gold text-teal-dark">
          <BadgePercent size={28} />
        </span>
        <div className="text-center sm:text-left">
          <p className="font-serif text-2xl sm:text-3xl text-gold leading-tight">
            10 % Online-Rabatt auf jede Bestellung
          </p>
          <p className="mt-1 text-cream/60 text-sm">
            Wird automatisch im Warenkorb abgezogen – gilt für Abholung & Lieferung.
          </p>
        </div>
      </motion.div>

      <div className="columns-1 md:columns-2 gap-16">
        {MENU.map((cat, i) => <Category key={cat.id} cat={cat} i={i} />)}
      </div>

      <div className="mt-14 border-t border-teal/15 pt-8 grid sm:grid-cols-2 gap-x-12 gap-y-8" data-testid="allergen-legend">
        <div>
          <div className="overline mb-4" style={{ color: "#123F41" }}>Allergenlegende</div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-teal/70 text-xs">
            {Object.entries(ALLERGENS).map(([k, v]) => (
              <span key={k}><b className="text-teal">{k}</b> = {v}</span>
            ))}
          </div>
        </div>
        <div>
          <div className="overline mb-4" style={{ color: "#123F41" }}>Zusatzstoffe</div>
          <div className="space-y-1.5 text-teal/70 text-xs">
            {Object.entries(ZUSATZ).map(([k, v]) => (
              <div key={k}><b className="text-teal">{k}</b> = {v}</div>
            ))}
          </div>
          <p className="mt-5 text-teal/50 text-xs max-w-sm">
            Kennzeichnung in eckigen Klammern hinter jedem Gericht. Alle Preise in Euro inkl. MwSt.
          </p>
        </div>
      </div>
    </div>
  </section>
);
